import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Form, Button, Table, Alert, Spinner, Badge } from 'react-bootstrap';
import apiClient from '../../services/apiClient';

interface ParkingSpotInfo {
  spotNumber?: string;
  floor?: string | number;
  zone?: string;
}

interface VehicleInfo {
  licensePlate?: string;
  vehicleInfo?: {
    type?: string;
    color?: string;
    brand?: string;
  };
  isVIP?: boolean;
}

interface ParkingSession {
  sessionId?: string;
  entryTime: string;
  status?: 'active' | 'completed' | 'cancelled';
  parkingSpot?: ParkingSpotInfo;
  vehicle?: VehicleInfo;
}

const OnsiteVehicles: React.FC = () => {
  const [sessions, setSessions] = useState<ParkingSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // 篩選條件（依據畫面示意提供基本可用）
  const today = useMemo(() => new Date(), []);
  const defaultStart = useMemo(() => {
    const d = new Date(today);
    d.setHours(0, 0, 0, 0);
    return d.toISOString().slice(0, 16); // yyyy-MM-ddTHH:mm
  }, [today]);
  const defaultEnd = useMemo(() => {
    const d = new Date(today);
    d.setHours(23, 59, 59, 999);
    return d.toISOString().slice(0, 16);
  }, [today]);

  const [filters, setFilters] = useState({
    parkingLot: '',
    vehicleType: '全部',
    lane: '全部',
    plateNumber: '',
    startTime: defaultStart,
    endTime: defaultEnd,
    sortBy: 'entryTime',
  });

  const loadSessions = async () => {
    try {
      setLoading(true);
      setError('');

      const params: any = {
        status: 'active',
        licensePlate: filters.plateNumber || undefined,
        startTime: filters.startTime,
        endTime: filters.endTime,
        lane: filters.lane !== '全部' ? filters.lane : undefined,
        lot: filters.parkingLot || undefined,
        limit: 100,
      };

      const response = await apiClient.get('/parking/sessions/active', { params });
      const data = response.data;

      if (data?.success) {
        const sessionsData = data?.data?.sessions || data?.sessions;
        setSessions(Array.isArray(sessionsData) ? sessionsData : []);
      } else {
        throw new Error(data?.message || '獲取在場車輛資料失敗');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || '載入失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    loadSessions();
  };

  const formatDuration = (entryTime: string) => {
    const diffMin = Math.max(
      0,
      Math.floor((Date.now() - new Date(entryTime).getTime()) / (1000 * 60))
    );
    const hours = Math.floor(diffMin / 60);
    const minutes = diffMin % 60;
    return `${hours}時 ${minutes}分`;
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">
          <i className="fas fa-car-side me-2" /> 在場車輛
        </h2>
        <Button variant="outline-primary" onClick={loadSessions} disabled={loading}>
          <i className="fas fa-sync-alt me-2" /> 重新整理
        </Button>
      </div>

      <Card className="mb-3">
        <Card.Header>篩選條件</Card.Header>
        <Card.Body>
          <Row className="gy-2">
            <Col xs={12} md={3}>
              <Form.Label>車場名稱</Form.Label>
              <Form.Select
                value={filters.parkingLot}
                onChange={(e) => setFilters({ ...filters, parkingLot: e.target.value })}
              >
                <option value="">全部</option>
              </Form.Select>
            </Col>
            <Col xs={12} md={3}>
              <Form.Label>車輛類型</Form.Label>
              <Form.Select
                value={filters.vehicleType}
                onChange={(e) => setFilters({ ...filters, vehicleType: e.target.value })}
              >
                <option value="全部">全部</option>
                <option value="car">汽車</option>
                <option value="motorcycle">機車</option>
                <option value="vip">VIP</option>
              </Form.Select>
            </Col>
            <Col xs={12} md={3}>
              <Form.Label>進出口</Form.Label>
              <Form.Select
                value={filters.lane}
                onChange={(e) => setFilters({ ...filters, lane: e.target.value })}
              >
                <option value="全部">全部</option>
              </Form.Select>
            </Col>
            <Col xs={12} md={3}>
              <Form.Label>車牌號碼</Form.Label>
              <Form.Control
                placeholder="輸入車牌"
                value={filters.plateNumber}
                onChange={(e) => setFilters({ ...filters, plateNumber: e.target.value })}
              />
            </Col>
            <Col xs={12} md={3}>
              <Form.Label>開始時間</Form.Label>
              <Form.Control
                type="datetime-local"
                value={filters.startTime}
                onChange={(e) => setFilters({ ...filters, startTime: e.target.value })}
              />
            </Col>
            <Col xs={12} md={3}>
              <Form.Label>結束時間</Form.Label>
              <Form.Control
                type="datetime-local"
                value={filters.endTime}
                onChange={(e) => setFilters({ ...filters, endTime: e.target.value })}
              />
            </Col>
            <Col xs={12} md={3}>
              <Form.Label>進場時間排序</Form.Label>
              <Form.Select
                value={filters.sortBy}
                onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
              >
                <option value="entryTime">進場時間</option>
              </Form.Select>
            </Col>
            <Col xs={12} md={3} className="d-flex align-items-end">
              <Button variant="primary" onClick={handleSearch} disabled={loading}>
                <i className="fas fa-search me-2" /> 查詢
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" className="mb-3">
          <i className="fas fa-exclamation-triangle me-2" /> {error}
        </Alert>
      )}

      <Card>
        <Card.Header>在場車輛清單</Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '250px' }}>
              <Spinner animation="border" role="status">
                <span className="visually-hidden">載入中...</span>
              </Spinner>
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-4">
              <i className="fas fa-car fa-3x text-muted mb-3"></i>
              <p className="text-muted">目前沒有在場車輛記錄</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>序號</th>
                  <th>車牌號碼</th>
                  <th>停車位</th>
                  <th>區域</th>
                  <th>進場時間</th>
                  <th>停車時長</th>
                  <th>狀態</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s, idx) => (
                  <tr key={s.sessionId || `${s.entryTime}-${idx}`}>
                    <td>{idx + 1}</td>
                    <td>{s.vehicle?.licensePlate || '—'}</td>
                    <td>{s.parkingSpot?.spotNumber || '—'}</td>
                    <td>{[s.parkingSpot?.floor, s.parkingSpot?.zone].filter(Boolean).join(' / ') || '—'}</td>
                    <td>{new Date(s.entryTime).toLocaleString()}</td>
                    <td>{formatDuration(s.entryTime)}</td>
                    <td>
                      <Badge bg={s.status === 'active' ? 'success' : s.status === 'completed' ? 'secondary' : 'warning'}>
                        {s.status || 'active'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default OnsiteVehicles;